import PropTypes from 'prop-types'
import gql from 'graphql-tag'
import { Query } from '@apollo/client/react/components'
import LinearProgress from '@material-ui/core/LinearProgress'
import Error from '@components/error'
import richTextFragment from '@components/renderers/rich-text.fragment'
import Program from '@layouts/program/index'
import programFragment from '@layouts/program/program.fragment'
import { fetchMultipleSSRData } from '@lib/ssr-fetch'
import Other from './other'

/**
 * Split from a single BySlug document: page + promotionalPage together were
 * near/over Contentful's 8KB query limit once shared rich-text embeds grew.
 */
export const OTHER_PAGE_QUERY = gql`
query OtherPageBySlug($slug: String, $preview: Boolean)  {
  pageCollection(limit: 1, preview: $preview, where: {slug: $slug})  {
    items {
      slug
      route2
      eyebrow
      headline
      metaDescription
      image {
        url
        description
      }
      content {
        ${richTextFragment}
      }
      showBackButton
    }
  }
}
`

export const OTHER_PROMO_QUERY = gql`
query OtherPromoBySlug($slug: String, $preview: Boolean)  {
  promotionalPageCollection(limit: 1, preview: $preview, where: {slug: $slug}) {
    items {
      ${programFragment}
    }
  }
}
`

/** @deprecated Use OTHER_PAGE_QUERY + OTHER_PROMO_QUERY via fetchOtherSSRData */
export const OTHER_QUERY = OTHER_PAGE_QUERY

function failedKeys(ssrErrors) {
  return new Set((ssrErrors || []).map((err) => err.key))
}

function collectionHasItem(collection) {
  return Boolean(collection?.items?.[0])
}

/**
 * Fetch page + promo documents separately, then merge for OtherWithData.
 *
 * On total or ambiguous failure, omit `data` (leave undefined) so OtherWithData
 * falls through to the client Query path — matching legacy fetchSSRData.
 * Never invent empty collections for a query that actually errored.
 */
export async function fetchOtherSSRData({ query }, fetchMultiple = fetchMultipleSSRData) {
  const result = await fetchMultiple({
    query,
    queries: [
      {
        key: 'page',
        graphqlQuery: OTHER_PAGE_QUERY,
        variables: query,
      },
      {
        key: 'promo',
        graphqlQuery: OTHER_PROMO_QUERY,
        variables: query,
      },
    ],
  })

  const failures = failedKeys(result.ssrErrors)
  const pageFailed = failures.has('page')
  const promoFailed = failures.has('promo')
  const pageCollection = result.page?.pageCollection
  const promotionalPageCollection = result.promo?.promotionalPageCollection

  if (pageFailed && promoFailed) {
    const message = result.ssrErrors.map((err) => err.message).join('; ')
    return {
      query,
      error: message,
      errorObject: { name: 'Error', message },
      ssrFetched: true,
    }
  }

  if (pageFailed || promoFailed) {
    // Keep SSR render only when the successful side actually has content.
    // If that side is empty, omit data so the client can retry the failed query
    // (e.g. promo-only slug when OTHER_PROMO_QUERY failed).
    const usablePage = !pageFailed && collectionHasItem(pageCollection)
    const usablePromo = !promoFailed && collectionHasItem(promotionalPageCollection)

    if (!usablePage && !usablePromo) {
      const message = (result.ssrErrors || [])
        .map((err) => err.message)
        .join('; ') || 'Other page SSR query failed'
      return {
        query,
        error: message,
        errorObject: { name: 'Error', message },
        ssrFetched: true,
        ssrErrors: result.ssrErrors,
      }
    }

    return {
      query,
      data: {
        ...(pageCollection ? { pageCollection } : {}),
        ...(promotionalPageCollection ? { promotionalPageCollection } : {}),
      },
      ssrFetched: true,
      ssrErrors: result.ssrErrors,
    }
  }

  return {
    query,
    data: {
      pageCollection,
      promotionalPageCollection,
    },
    ssrFetched: true,
  }
}

export default function OtherWithData(props) {
  const {
    query, asPath, ssrData, error: ssrError,
  } = props

  // Helper to render with data
  const renderWithData = (data) => {
    let Component = Other
    let response = data?.pageCollection?.items?.[0]

    if (response) {
      const routeFromData = response?.route2
      return asPath.includes(routeFromData) ? (
        <Component {...response} {...props} />
      ) : <Error statusCode={404} />
    }

    response = data?.promotionalPageCollection?.items?.[0]
    Component = Program
    return response ? (
      <Component {...response} {...props} />
    ) : <Error statusCode={404} />
  }

  // SSR payload present — including empty collections from successful queries.
  // When fetchOtherSSRData omits `data` after errors, ssrData is undefined and
  // we fall through to client Queries (legacy fetchSSRData behavior).
  if (ssrData !== undefined) {
    return renderWithData(ssrData)
  }

  // Client fallback: two documents keep each under Contentful's 8KB limit
  return (
    <Query query={OTHER_PAGE_QUERY} variables={query}>
      {({
        loading: pageLoading, error: pageError, data: pageData,
      }) => (
        <Query query={OTHER_PROMO_QUERY} variables={query}>
          {({
            loading: promoLoading, error: promoError, data: promoData,
          }) => {
            if (pageError && promoError) {
              return <Error error={pageError || ssrError} displayErrorType="page" />
            }
            if (pageLoading || promoLoading) {
              return <LinearProgress color="secondary" />
            }
            return renderWithData({
              pageCollection: pageData?.pageCollection,
              promotionalPageCollection: promoData?.promotionalPageCollection,
            })
          }}
        </Query>
      )}
    </Query>
  )
}

OtherWithData.propTypes = {
  query: PropTypes.object,
  asPath: PropTypes.string,
  ssrData: PropTypes.object,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
}
